(() => {
  if (customElements.get('sacred-restock-alert')) return;

  function waitForSwym() {
    return new Promise((resolve, reject) => {
      let timer;
      let settled = false;
      const cleanup = () => {
        clearTimeout(timer);
        const index = window.SwymCallbacks.indexOf(ready);
        if (index !== -1) window.SwymCallbacks.splice(index, 1);
      };
      const ready = (api) => {
        if (settled || typeof api?.sendWatchlist !== 'function') return;
        settled = true;
        clearTimeout(timer);
        Promise.resolve().then(cleanup);
        resolve(api);
      };
      window.SwymCallbacks = window.SwymCallbacks || [];
      timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        cleanup();
        reject(new Error('Restock signup is unavailable'));
      }, 8000);
      window.SwymCallbacks.push(ready);
      if (settled) cleanup();
    });
  }

  customElements.define('sacred-restock-alert', class extends HTMLElement {
    connectedCallback() {
      try {
        this.variants = JSON.parse(this.dataset.variants);
      } catch {
        this.hidden = true;
        return;
      }
      this.button = this.querySelector('[data-restock-open]');
      this.panel = this.querySelector('[data-restock-panel]');
      this.signupForm = this.querySelector('[data-restock-form]');
      this.email = this.querySelector('[data-restock-email]');
      this.submitButton = this.querySelector('[data-restock-submit]');
      this.status = this.querySelector('[data-restock-status]');
      this.contact = this.querySelector('[data-restock-contact]');
      this.fields = this.querySelector('[data-restock-fields]');
      this.confirmation = this.querySelector('[data-restock-confirmed]');
      this.manualNote = this.querySelector('[data-restock-manual-note]');
      this.variantField = this.querySelector('[data-restock-variant]');
      this.optionText = this.querySelector('[data-restock-option]');
      this.urlField = this.querySelector('[data-restock-url]');
      this.returnField = this.signupForm.querySelector('[name="return_to"]');
      this.manualMode = !!this.querySelector('[data-restock-native-errors]');
      this.confirmedVariant = this.confirmation ? this.dataset.selectedVariant : null;
      this.form = document.getElementById(this.dataset.formId);
      this.onVariantInput = (event) => {
        if (event.target.name === 'id') this.selectVariant(event.target.value);
      };
      this.form?.addEventListener('change', this.onVariantInput);
      this.onOpen = this.openSignup.bind(this);
      this.button.addEventListener('click', this.onOpen);
      this.onSubmit = this.submitSignup.bind(this);
      this.signupForm.addEventListener('submit', this.onSubmit);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (String(data?.sectionId) !== this.dataset.sectionId) return;
          const variant = data.variant;
          const saved = this.variants.find((item) => String(item.id) === String(variant?.id));
          if (saved && typeof variant.available === 'boolean') saved.available = variant.available;
          this.selectVariant(variant?.id);
        });
      }
      this.selectVariant(this.form?.querySelector('[name="id"]')?.value || this.dataset.selectedVariant);
      if (this.manualMode) this.panel.hidden = false;
      this.button.setAttribute('aria-expanded', String(!this.panel.hidden));
    }

    disconnectedCallback() {
      this.form?.removeEventListener('change', this.onVariantInput);
      this.button?.removeEventListener('click', this.onOpen);
      this.signupForm?.removeEventListener('submit', this.onSubmit);
      this.unsubscribe?.();
    }

    selectVariant(id) {
      this.variant = this.variants.find((item) => String(item.id) === String(id));
      this.hidden = !this.variant || this.variant.available;
      this.status.textContent = '';
      this.contact.hidden = false;
      if (!this.variant) return;
      const productUrl = new URL(this.dataset.productUrl, window.location.origin);
      productUrl.searchParams.set('variant', this.variant.id);
      this.variantField.value = `${this.variant.title} (ID: ${this.variant.id})`;
      if (this.optionText) this.optionText.textContent = this.variant.title;
      this.urlField.value = productUrl.href;
      if (this.returnField) this.returnField.value = `${productUrl.pathname}${productUrl.search}#${this.signupForm.id}`;
      const confirmed = String(this.variant.id) === this.confirmedVariant;
      if (this.confirmation) this.confirmation.hidden = !confirmed;
      this.fields.hidden = confirmed;
      if (confirmed) this.panel.hidden = false;
      this.button.setAttribute('aria-expanded', String(!this.panel.hidden));
      this.syncMode();
    }

    openSignup() {
      this.panel.hidden = !this.panel.hidden;
      this.button.setAttribute('aria-expanded', String(!this.panel.hidden));
      if (!this.panel.hidden && !this.fields.hidden) this.email.focus();
    }

    enableManualMode() {
      this.manualMode = true;
      this.panel.hidden = false;
      this.button.setAttribute('aria-expanded', 'true');
      this.status.textContent = '';
      this.syncMode();
      this.email.focus();
    }

    syncMode() {
      this.submitButton.textContent = this.manualMode ? this.dataset.manualSubmitText : this.dataset.submitText;
      this.manualNote.hidden = !this.manualMode;
    }

    async submitSignup(event) {
      const variant = this.variant;
      if (this.busy || !variant || variant.available) {
        event.preventDefault();
        return;
      }
      if (this.manualMode) return;
      event.preventDefault();
      if (!this.signupForm.reportValidity()) return;
      this.busy = true;
      this.submitButton.disabled = true;
      this.status.textContent = this.dataset.loadingText;
      try {
        const api = await waitForSwym();
        if (!this.isConnected || this.variant?.id !== variant.id || this.variant.available) return;
        const payload = {
          epi: variant.id,
          empi: this.dataset.productId,
          du: new URL(this.dataset.productUrl, window.location.origin).href,
          pr: variant.price,
          iu: variant.image,
          et: 8,
        };
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => reject(new Error('Restock service timed out')), 15000);
          const finish = (callback) => (result) => {
            clearTimeout(timeout);
            callback(result);
          };
          try {
            api.sendWatchlist(this.email.value.trim(), 'email', payload, finish(resolve), finish(reject), 0);
          } catch (error) {
            clearTimeout(timeout);
            reject(error);
          }
        });
        if (!this.isConnected || this.variant?.id !== variant.id || this.variant.available) return;
        this.status.textContent = this.dataset.successText;
        this.panel.hidden = true;
        this.button.setAttribute('aria-expanded', 'false');
      } catch (error) {
        console.warn('Restock signup unavailable:', error?.message || 'Service unavailable');
        if (!this.isConnected || this.variant?.id !== variant.id) return;
        this.enableManualMode();
        this.status.textContent = this.dataset.errorText;
      } finally {
        this.busy = false;
        this.submitButton.disabled = false;
      }
    }
  });
})();
